import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback
} from "react";
import PropTypes from "prop-types";
import whatsappMicroserviceService from "../services/whatsappMicroserviceService";
import whatsappPanelService from "../services/whatsappPanelService";
import '../style/components/WhatsAppChatModal.css';

// ─────────────────────────────────────────
//  Constants
// ─────────────────────────────────────────
const POLLING_MS        = 15000;
const INACTIVE_TIMEOUT  = 12000; // ms before transparency kicks in
const MIN_WIDTH         = 340;
const MIN_HEIGHT        = 440;
const LS_KEY_STATE      = "wsp_widget_state";   // localStorage key

// ─────────────────────────────────────────
//  Pure helpers
// ─────────────────────────────────────────
const stripNonDigits = (phone) =>
  phone ? String(phone).replace(/\D/g, "") : "";

// Misma lógica que toWhatsAppPhone() en el microservicio (validators.js).
// Normaliza cualquier formato argentino al JID completo de 13 dígitos.
const toWhatsAppPhone = (phone) => {
  const d = stripNonDigits(phone);
  if (!d) return "";
  if (d.startsWith("549") && d.length === 13) return d;
  if (d.startsWith("54")  && d.length === 12) return "549" + d.slice(2);
  if (d.startsWith("9")   && d.length === 11) return "54"  + d;
  if (d.length === 10)                         return "549" + d;
  return d;
};

const normalizePhone = (phone) => toWhatsAppPhone(phone);

const formatClock = (dateValue) => {
  if (!dateValue) return "--:--";
  const d = new Date(dateValue);
  return isNaN(d.getTime())
    ? "--:--"
    : d.toLocaleTimeString("es-AR", { hour: "2-digit", minute: "2-digit" });
};

const getStatusLabel = (status) => {
  switch (status) {
    case "sent":
      return "Enviado";
    case "delivered":
      return "Entregado";
    case "read":
      return "Leído";
    case "failed":
      return "Falló";
    case "queued":
      return "En cola";
    default:
      return "";
  }
};

const mapDtoStatus = (estado) => {
  if (!estado) return "";
  if (estado === 'ENVIADO') return 'sent';
  if (estado === 'ENTREGADO') return 'delivered';
  if (estado === 'LEIDO') return 'read';
  if (estado === 'FALLIDO') return 'failed';
  if (estado === 'EN_COLA') return 'queued';
  return estado.toLowerCase();
};

const areaDesdeRuta = (pathname) => {
  if (pathname.includes('/art/')) return 'ART';
  if (pathname.includes('/rti/')) return 'RTI';
  if (pathname.includes('/judicial/')) return 'JUDICIAL';
  if (pathname.includes('/recepcion/')) return 'RECEPCION';
  return 'ADMIN';
};

/** Returns SVG tick element matching WhatsApp look */
const StatusTicks = ({ status }) => {
  if (status === "failed")
    return <span className="wsp-tick wsp-tick--failed" title="Error">⚠</span>;

  if (status === "read")
    return (
      <span className="wsp-tick wsp-tick--read" title="Leído">
        <svg viewBox="0 0 16 11" className="wsp-tick-svg">
          <path d="M11.071.653a.5.5 0 0 1 .707.707l-6 6a.5.5 0 0 1-.64.058L.646 4.062a.5.5 0 1 1 .708-.707l4.14 3.31 5.577-6.012z"/>
          <path d="M15.07.653a.5.5 0 0 1 .708.707l-6 6a.5.5 0 0 1-.64.058l-.894-.716a.5.5 0 1 1 .628-.778l.547.437 5.651-5.708z"/>
        </svg>
      </span>
    );

  if (status === "delivered")
    return (
      <span className="wsp-tick wsp-tick--delivered" title="Entregado">
        <svg viewBox="0 0 16 11" className="wsp-tick-svg">
          <path d="M11.071.653a.5.5 0 0 1 .707.707l-6 6a.5.5 0 0 1-.64.058L.646 4.062a.5.5 0 1 1 .708-.707l4.14 3.31 5.577-6.012z"/>
          <path d="M15.07.653a.5.5 0 0 1 .708.707l-6 6a.5.5 0 0 1-.64.058l-.894-.716a.5.5 0 1 1 .628-.778l.547.437 5.651-5.708z"/>
        </svg>
      </span>
    );

  if (status === "sent")
    return (
      <span className="wsp-tick wsp-tick--sent" title="Enviado">
        <svg viewBox="0 0 9 11" className="wsp-tick-svg">
          <path d="M7.071.653a.5.5 0 0 1 .707.707l-5 5a.5.5 0 0 1-.64.058L.646 4.062a.5.5 0 1 1 .708-.707l1.14.913L7.07.653z"/>
        </svg>
      </span>
    );

  return null;
};

// ─────────────────────────────────────────
//  Notification sound  (Web Audio API — no file needed)
// ─────────────────────────────────────────
let audioCtx = null;
const playNotification = () => {
  try {
    if (!audioCtx) audioCtx = new (window.AudioContext || window.webkitAudioContext)();
    const o = audioCtx.createOscillator();
    const g = audioCtx.createGain();
    o.connect(g);
    g.connect(audioCtx.destination);
    o.type = "sine";
    o.frequency.setValueAtTime(880, audioCtx.currentTime);
    o.frequency.exponentialRampToValueAtTime(440, audioCtx.currentTime + 0.15);
    g.gain.setValueAtTime(0.25, audioCtx.currentTime);
    g.gain.exponentialRampToValueAtTime(0.001, audioCtx.currentTime + 0.4);
    o.start();
    o.stop(audioCtx.currentTime + 0.4);
  } catch (_) {}
};

// ─────────────────────────────────────────
//  Component
// ─────────────────────────────────────────
function WhatsAppChatModal({ isOpen, onClose, clientName, phone }) {
  // ── State ──────────────────────────────
  const [messages,      setMessages]      = useState([]);
  const [input,         setInput]         = useState("");
  const [loading,       setLoading]       = useState(false);
  const [sending,       setSending]       = useState(false);
  const [error,         setError]         = useState("");
  const [sessionActive, setSessionActive] = useState(false);
  const [isTyping,      setIsTyping]      = useState(false);
  const [unreadCount,   setUnreadCount]   = useState(0);

  // Persist minimized / position to localStorage
  const [isMinimized, setIsMinimized] = useState(() => {
    try {
      const s = JSON.parse(localStorage.getItem(LS_KEY_STATE) || "{}");
      return s.isMinimized ?? false;
    } catch { return false; }
  });

  const [position, setPosition] = useState(() => {
    try {
      const s = JSON.parse(localStorage.getItem(LS_KEY_STATE) || "{}");
      if (s.x != null && s.y != null) return { x: s.x, y: s.y };
    } catch {}
    return {
      x: Math.max(24, window.innerWidth  - 500),
      y: Math.max(24, window.innerHeight - 760)
    };
  });

  const [size, setSize] = useState({ width: 460, height: 680 });

  // Inactive / transparency
  const [isInactive, setIsInactive]   = useState(false);
  const inactiveTimerRef              = useRef(null);

  // Drag
  const [isDragging, setIsDragging]   = useState(false);
  const [dragOffset, setDragOffset]   = useState({ x: 0, y: 0 });

  // Resize
  const [isResizing,   setIsResizing]   = useState(false);
  const [resizeOrigin, setResizeOrigin] = useState({ x: 0, y: 0, width: 460, height: 680 });

  const messagesContainerRef = useRef(null);
  const prevMsgCountRef      = useRef(0);
  const inputRef             = useRef(null);

  const normalizedPhone = useMemo(() => normalizePhone(phone), [phone]);

  // ── Persist to localStorage ────────────
  useEffect(() => {
    try {
      localStorage.setItem(
        LS_KEY_STATE,
        JSON.stringify({ isMinimized, x: position.x, y: position.y })
      );
    } catch {}
  }, [isMinimized, position]);

  // ── Map + filter + sort messages ───────
  const mappedMessages = useMemo(() => {
    return [...messages]
      .map((item) => ({
        id:        item.id,
        phone:     normalizedPhone,
        message:   item.texto || item.message,
        direction: item.direccion === 'ENTRANTE' ? 'incoming' : (item.direccion === 'SALIENTE' ? 'outgoing' : item.direction),
        status:    mapDtoStatus(item.estado) || item.status,
        timestamp: item.fechaCreacion || item.timestamp || item.createdAt || item.updatedAt
      }))
      .sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
  }, [messages, normalizedPhone]);

  // ── Scroll to bottom ───────────────────
  const scrollToBottom = useCallback(() => {
    if (!messagesContainerRef.current) return;
    messagesContainerRef.current.scrollTo({
      top:      messagesContainerRef.current.scrollHeight,
      behavior: "smooth"
    });
  }, []);

  // ── Load messages ──────────────────────
  const loadMessagesAndHealth = useCallback(async () => {
    if (!normalizedPhone) return;
    try {
      const area = areaDesdeRuta(typeof window !== 'undefined' ? window.location.pathname : '');
      const conversacion = await whatsappPanelService.obtenerOCrearPorTelefono({
        telefono: normalizedPhone,
        nombre: clientName || "Cliente",
        area: area
      });

      const [dbMessages, health] = await Promise.all([
        whatsappPanelService.listarMensajes(conversacion.id),
        whatsappMicroserviceService.getHealth()
      ]);

      setMessages(Array.isArray(dbMessages) ? dbMessages : []);
      setSessionActive(Boolean(health?.whatsapp?.sessionActive));
      setError("");
    } catch {
      setError("No se pudo sincronizar el chat.");
    }
  }, [normalizedPhone, clientName]);

  // ── Initial load + polling ─────────────
  useEffect(() => {
    if (!isOpen) return;
    setLoading(true);
    loadMessagesAndHealth().finally(() => {
      setLoading(false);
      setTimeout(scrollToBottom, 100);
    });

    const interval = setInterval(loadMessagesAndHealth, POLLING_MS);
    return () => clearInterval(interval);
  }, [isOpen, normalizedPhone, loadMessagesAndHealth, scrollToBottom]);

  // ── Auto scroll + new-message detection ─
  useEffect(() => {
    if (!isOpen) return;

    const prev = prevMsgCountRef.current;
    const curr = mappedMessages.length;

    if (curr > prev && prev > 0) {
      // New incoming message(s) arrived
      const lastMsg = mappedMessages[curr - 1];
      if (lastMsg?.direction === "incoming") {
        playNotification();
        if (isMinimized) {
          setUnreadCount((c) => c + (curr - prev));
        }
        // "typing" shimmer before the message appears
        setIsTyping(true);
        setTimeout(() => setIsTyping(false), 900);
      }
    }
    prevMsgCountRef.current = curr;
    scrollToBottom();
  }, [mappedMessages, isOpen, isMinimized, scrollToBottom]);

  // ── Clear unread when expanding ────────
  useEffect(() => {
    if (!isMinimized) setUnreadCount(0);
  }, [isMinimized]);

  // ── Inactive transparency ──────────────
  const resetInactiveTimer = useCallback(() => {
    setIsInactive(false);
    clearTimeout(inactiveTimerRef.current);
    inactiveTimerRef.current = setTimeout(
      () => setIsInactive(true),
      INACTIVE_TIMEOUT
    );
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    resetInactiveTimer();
    return () => clearTimeout(inactiveTimerRef.current);
  }, [isOpen, resetInactiveTimer]);

  // ── Clamp position on resize ───────────
  useEffect(() => {
    if (!isOpen) return;
    const clampedHeight = isMinimized ? 72 : size.height;
    const maxX = Math.max(12, window.innerWidth  - size.width    - 12);
    const maxY = Math.max(12, window.innerHeight - clampedHeight - 12);
    setPosition((prev) => ({
      x: Math.min(Math.max(12, prev.x), maxX),
      y: Math.min(Math.max(12, prev.y), maxY)
    }));
  }, [isOpen, size.width, size.height, isMinimized]);

  // ── Drag + Resize listeners ────────────
  useEffect(() => {
    if (!isDragging && !isResizing) return;

    const onMove = (e) => {
      if (isDragging) {
        const maxX = Math.max(12, window.innerWidth  - size.width    - 12);
        const maxY = Math.max(12, window.innerHeight - (isMinimized ? 72 : size.height) - 12);
        setPosition({
          x: Math.min(Math.max(12, e.clientX - dragOffset.x), maxX),
          y: Math.min(Math.max(12, e.clientY - dragOffset.y), maxY)
        });
      }
      if (isResizing) {
        const dX = e.clientX - resizeOrigin.x;
        const dY = e.clientY - resizeOrigin.y;
        setSize({
          width:  Math.max(MIN_WIDTH,  Math.min(window.innerWidth  - 24, resizeOrigin.width  + dX)),
          height: Math.max(MIN_HEIGHT, Math.min(window.innerHeight - 24, resizeOrigin.height + dY))
        });
      }
    };

    const onUp = () => { setIsDragging(false); setIsResizing(false); };

    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup",   onUp);
    return () => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup",   onUp);
    };
  }, [isDragging, isResizing, dragOffset, resizeOrigin, size.width, size.height, isMinimized]);

  // ── Send message ───────────────────────
  const handleSend = async () => {
    const text = input.trim();
    if (!text || !normalizedPhone || sending) return;

    const optimistic = {
      id:        `tmp-${Date.now()}`,
      phone:     normalizedPhone,
      message:   text,
      direction: "outgoing",
      status:    "sent",
      timestamp: new Date().toISOString()
    };

    setInput("");
    setSending(true);
    setError("");
    setMessages((prev) => [...prev, optimistic]);
    resetInactiveTimer();

    try {
      const area = areaDesdeRuta(typeof window !== 'undefined' ? window.location.pathname : '');
      await whatsappPanelService.enviarMensaje({
        telefonoCliente: normalizedPhone,
        nombreCliente: clientName || "Cliente",
        area: area,
        texto: text
      });
      await loadMessagesAndHealth();
    } catch {
      setMessages((prev) =>
        prev.map((m) => m.id === optimistic.id ? { ...m, status: "failed", estado: "FALLIDO" } : m)
      );
      setError("No se pudo enviar el mensaje. Intentá de nuevo.");
    } finally {
      setSending(false);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSend(); }
  };

  const handleHeaderMouseDown = (e) => {
    if (e.target.closest("button")) return;
    setIsDragging(true);
    setDragOffset({ x: e.clientX - position.x, y: e.clientY - position.y });
    resetInactiveTimer();
  };

  const handleResizeMouseDown = (e) => {
    e.preventDefault();
    setIsResizing(true);
    setResizeOrigin({ x: e.clientX, y: e.clientY, width: size.width, height: size.height });
  };

  const toggleMinimize = () => {
    setIsMinimized((v) => !v);
    resetInactiveTimer();
  };

  if (!isOpen) return null;

  return (
    <aside
      className={[
        "wsp-widget",
        isMinimized ? "wsp-widget--minimized" : "",
        isInactive  ? "wsp-widget--inactive"  : ""
      ].filter(Boolean).join(" ")}
      style={{
        left:   `${position.x}px`,
        top:    `${position.y}px`,
        width:  `${size.width}px`,
        height: isMinimized ? "72px" : `${size.height}px`
      }}
      role="dialog"
      aria-modal="false"
      aria-label={`Chat WhatsApp con ${clientName}`}
      onMouseEnter={resetInactiveTimer}
      onMouseMove={resetInactiveTimer}
    >
      {/* ── HEADER ── */}
      <header className="wsp-header" onMouseDown={handleHeaderMouseDown}>
        {/* Avatar */}
        <div className="wsp-avatar">
          {(clientName || "C").charAt(0).toUpperCase()}
        </div>

        {/* Info */}
        <div className="wsp-header-info">
          <h3 className="wsp-header-name">{clientName || "Cliente"}</h3>
          <p className="wsp-header-status">
            {normalizedPhone || ""}
            {!sessionActive && (
              <span className="wsp-session-warn"> · sesión inactiva</span>
            )}
          </p>
        </div>

        {/* Actions */}
        <div className="wsp-header-actions">
          {/* Unread badge visible only when minimized */}
          {isMinimized && unreadCount > 0 && (
            <span className="wsp-unread-badge">{unreadCount > 99 ? "99+" : unreadCount}</span>
          )}

          <button
            className="wsp-icon-btn"
            onClick={toggleMinimize}
            title={isMinimized ? "Expandir" : "Minimizar"}
            aria-label={isMinimized ? "Expandir chat" : "Minimizar chat"}
          >
            {isMinimized ? (
              // Expand icon
              <svg viewBox="0 0 24 24" className="wsp-icon">
                <path d="M7 14H5v5h5v-2H7v-3zm-2-4h2V7h3V5H5v5zm12 7h-3v2h5v-5h-2v3zM14 5v2h3v3h2V5h-5z"/>
              </svg>
            ) : (
              // Minimize icon
              <svg viewBox="0 0 24 24" className="wsp-icon">
                <path d="M19 13H5v-2h14v2z"/>
              </svg>
            )}
          </button>

          <button
            className="wsp-icon-btn wsp-icon-btn--close"
            onClick={onClose}
            title="Cerrar"
            aria-label="Cerrar chat"
          >
            <svg viewBox="0 0 24 24" className="wsp-icon">
              <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
            </svg>
          </button>
        </div>
      </header>

      {/* ── BODY (hidden when minimized) ── */}
      {!isMinimized && (
        <>
          {/* Messages area */}
          <section
            className="wsp-body"
            ref={messagesContainerRef}
            aria-live="polite"
            aria-label="Conversación"
          >
            {/* WhatsApp-style background pattern */}
            <div className="wsp-bg-pattern" aria-hidden="true" />

            {loading && (
              <div className="wsp-state-msg">
                <div className="wsp-spinner" />
                <span>Cargando conversación…</span>
              </div>
            )}

            {!loading && mappedMessages.length === 0 && (
              <div className="wsp-state-msg wsp-state-msg--empty">
                <svg viewBox="0 0 24 24" className="wsp-empty-icon">
                  <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2z"/>
                </svg>
                <p>No hay mensajes con este contacto</p>
                <small>{normalizedPhone || "Sin teléfono registrado"}</small>
              </div>
            )}

            {!loading && mappedMessages.map((msg, idx) => {
              const out = msg.direction === "outgoing";
              const prevMsg = mappedMessages[idx - 1];
              const showDate = !prevMsg || (
                new Date(msg.timestamp).toDateString() !==
                new Date(prevMsg.timestamp).toDateString()
              );

              return (
                <React.Fragment key={msg.id}>
                  {/* Date separator */}
                  {showDate && (
                    <div className="wsp-date-sep">
                      <span>{new Date(msg.timestamp).toLocaleDateString("es-AR", {
                        weekday: "long", day: "numeric", month: "long"
                      })}</span>
                    </div>
                  )}

                  <article className={`wsp-row ${out ? "wsp-row--out" : "wsp-row--in"}`}>
                    <div
                      className={[
                        "wsp-bubble",
                        out ? "wsp-bubble--out" : "wsp-bubble--in",
                        msg.status === "failed" ? "wsp-bubble--failed" : ""
                      ].filter(Boolean).join(" ")}
                    >
                      <p className="wsp-bubble-text">{msg.message}</p>
                      <footer className="wsp-bubble-meta">
                        <time dateTime={msg.timestamp}>{formatClock(msg.timestamp)}</time>
                        {out && (
                          <span className="wsp-bubble-status" title={getStatusLabel(msg.status)}>
                            {getStatusLabel(msg.status)}
                            <StatusTicks status={msg.status} />
                          </span>
                        )}
                      </footer>
                    </div>
                  </article>
                </React.Fragment>
              );
            })}

            {/* "Escribiendo…" indicator */}
            {isTyping && (
              <div className="wsp-row wsp-row--in">
                <div className="wsp-bubble wsp-bubble--in wsp-typing">
                  <span className="wsp-typing-dot" />
                  <span className="wsp-typing-dot" />
                  <span className="wsp-typing-dot" />
                </div>
              </div>
            )}
          </section>

          {/* Error banner */}
          {error && (
            <div className="wsp-error-bar" role="alert">
              <svg viewBox="0 0 24 24" className="wsp-error-icon">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
              </svg>
              {error}
            </div>
          )}

          {/* Sending indicator */}
          {sending && (
            <div className="wsp-sending-bar">
              <div className="wsp-sending-dots">
                <span /><span /><span />
              </div>
              <small>Enviando…</small>
            </div>
          )}

          {/* Footer / Input */}
          <footer className="wsp-footer">
            <textarea
              ref={inputRef}
              className="wsp-input"
              rows={1}
              value={input}
              onChange={(e) => { setInput(e.target.value); resetInactiveTimer(); }}
              onKeyDown={handleKeyDown}
              placeholder="Escribí un mensaje…"
              aria-label="Escribir mensaje"
              disabled={sending}
            />

            <button
              className={`wsp-send-btn ${input.trim() && !sending ? "wsp-send-btn--active" : ""}`}
              onClick={handleSend}
              disabled={!input.trim() || sending || !normalizedPhone}
              aria-label="Enviar mensaje"
            >
              <svg viewBox="0 0 24 24" className="wsp-send-icon">
                <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"/>
              </svg>
            </button>
          </footer>

          {/* Resize handle */}
          <button
            className="wsp-resize-handle"
            onMouseDown={handleResizeMouseDown}
            aria-label="Redimensionar"
          />
        </>
      )}
    </aside>
  );
}

WhatsAppChatModal.propTypes = {
  isOpen:     PropTypes.bool.isRequired,
  onClose:    PropTypes.func.isRequired,
  clientName: PropTypes.string,
  phone:      PropTypes.string
};

WhatsAppChatModal.defaultProps = {
  clientName: "Cliente",
  phone:      ""
};

export default WhatsAppChatModal;
